/**
 * Activity row. Screen spec S16 zone 3, and the last five lines on S7.
 *
 * One line per thing that happened, and every line is a sentence: "Deployed
 * Add print view to recipe pages", "Added a key: RESEND_API_KEY". No user
 * agent strings, no addresses, no identifiers. An audit log is a different
 * product and it is not this one.
 *
 * The glyph is a shape per kind, and it is deliberately NOT a status color. A
 * deploy that happened is not a state, it is a fact in the past, and colouring
 * the feed would put four saturated things on a screen whose whole argument is
 * that saturation means the app needs attention right now.
 *
 * The time is mono and right aligned, so a column of them reads down as one
 * timeline, and it follows the product's one time rule: relative under seven
 * days, an absolute date beyond it.
 */

import { whenLabel } from './DeployRow';
import { t } from '../../lib/i18n/t';
import type { ActivityEvent, ActivityKind } from '../../lib/types';
import '../../styles/components/activity-row.css';

type MarkShape = 'disc' | 'square' | 'diamond' | 'triangle';

/** Four shapes for eight kinds: what happened to what, not a taxonomy. */
const MARK: Record<ActivityKind, MarkShape> = {
  deploy: 'disc',
  error: 'triangle',
  database: 'square',
  files: 'square',
  keys: 'square',
  'add-on': 'diamond',
  account: 'diamond',
  graduation: 'diamond',
};

function Mark({ kind }: { kind: ActivityKind }) {
  const shape = MARK[kind];
  return (
    <span className="actr__mark" aria-hidden="true">
      <svg viewBox="0 0 16 16" width="9" height="9" focusable="false">
        {shape === 'disc' ? <circle cx="8" cy="8" r="6" fill="currentColor" /> : null}
        {shape === 'square' ? <rect x="2.5" y="2.5" width="11" height="11" fill="currentColor" /> : null}
        {shape === 'diamond' ? <path d="M8 1.5 14.5 8 8 14.5 1.5 8Z" fill="currentColor" /> : null}
        {shape === 'triangle' ? <path d="M8 2 15 14H1Z" fill="currentColor" /> : null}
      </svg>
    </span>
  );
}

export interface ActivityRowProps {
  event: ActivityEvent;
}

export function ActivityRow({ event }: ActivityRowProps) {
  return (
    <li className="actr">
      <Mark kind={event.kind} />
      <span className="actr__say">
        {t(event.title)}
        {event.detail ? <span className="actr__detail mono">{event.detail}</span> : null}
      </span>
      <span className="actr__when mono">{whenLabel(event.at)}</span>
    </li>
  );
}

export function ActivityList({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <ul className="actlist" aria-label={label}>
      {children}
    </ul>
  );
}
